import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Plus, ShieldCheck, Eye, Download, QrCode, Prohibit } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  listAttestations, createAttestation, issueAttestation, revokeAttestation,
  listCmcPayments, listCooperators,
  getAttestationHtmlUrl, getAttestationPdfUrl, getAttestationQrUrl,
  ATTESTATION_STATUSES,
} from '../../services/cmcApi';
import { batchIssueAttestations, batchRevokeAttestations, batchAttestPayments } from '../../services/api';
import { CmcPageHeader, CmcStatusBadge, CmcEmpty, fmtDate } from '../../components/cmc/CmcCommon';
import { Checkbox } from '../../components/ui/checkbox';

const emptyForm = {
  payment_id: '',
  title: 'Attestation de paiement coopératif',
  public_amount_visibility: 'masked',
};

export default function CmcAttestationsPage() {
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [cooperators, setCooperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialog, setQrDialog] = useState(null);
  const [revokeDialog, setRevokeDialog] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchRevokeOpen, setBatchRevokeOpen] = useState(false);
  const [batchRevokeReason, setBatchRevokeReason] = useState('');
  const [batchBusy, setBatchBusy] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectedByStatus = (status) => Array.from(selectedIds).filter((id) => items.find((a) => a.id === id)?.status === status);

  const doBatchIssue = async () => {
    const ids = selectedByStatus('draft');
    if (!ids.length) { toast.error("Aucun brouillon sélectionné"); return; }
    setBatchBusy(true);
    try {
      const r = await batchIssueAttestations(ids);
      toast.success(`${r.data.issued.length} attestation(s) émise(s), ${r.data.skipped.length} ignorée(s)`);
      clearSelection();
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur batch'); }
    setBatchBusy(false);
  };

  const doBatchRevoke = async () => {
    if (!batchRevokeReason.trim()) { toast.error('Motif requis'); return; }
    const ids = selectedByStatus('issued');
    if (!ids.length) { toast.error("Aucune attestation émise sélectionnée"); return; }
    setBatchBusy(true);
    try {
      const r = await batchRevokeAttestations(ids, batchRevokeReason.trim());
      toast.success(`${r.data.revoked.length} révoquée(s), ${r.data.skipped.length} ignorée(s)`);
      setBatchRevokeOpen(false);
      setBatchRevokeReason('');
      clearSelection();
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur batch'); }
    setBatchBusy(false);
  };

  const doBatchAttestPaidPayments = async () => {
    const paidIds = payments.map((p) => p.id);
    if (!paidIds.length) { toast.error('Aucun paiement payé disponible'); return; }
    if (!window.confirm(`Créer ${paidIds.length} attestation(s) brouillon pour tous les paiements payés ?`)) return;
    setBatchBusy(true);
    try {
      const r = await batchAttestPayments(paidIds);
      toast.success(`${r.data.created.length} attestation(s) créée(s), ${r.data.skipped.length} ignorée(s)`);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur batch'); }
    setBatchBusy(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p, c] = await Promise.all([
        listAttestations({ limit: 200 }),
        listCmcPayments({ status: 'paid', limit: 200 }),
        listCooperators({ limit: 200 }),
      ]);
      setItems(a.data.items || []);
      setPayments(p.data.items || []);
      setCooperators(c.data.items || []);
    } catch {
      toast.error('Erreur de chargement des attestations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submit = async () => {
    if (!form.payment_id) { toast.error('Sélectionnez un paiement payé'); return; }
    setSaving(true);
    try {
      await createAttestation(form);
      toast.success('Attestation créée (brouillon)');
      setDialogOpen(false);
      setForm(emptyForm);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || 'Erreur');
    } finally { setSaving(false); }
  };

  const onIssue = async (a) => {
    try { await issueAttestation(a.id); toast.success('Attestation émise + QR généré'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  const onRevoke = async () => {
    if (!revokeReason) { toast.error('Motif requis'); return; }
    setSaving(true);
    try {
      await revokeAttestation(revokeDialog.id, { reason: revokeReason });
      toast.success('Attestation révoquée');
      setRevokeDialog(null);
      setRevokeReason('');
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const coopMap = Object.fromEntries(cooperators.map((c) => [c.id, c]));
  const paymentMap = Object.fromEntries(payments.map((p) => [p.id, p]));
  const coopNameByPaymentId = (paymentId) => {
    const p = paymentMap[paymentId];
    if (!p) return paymentId;
    const c = coopMap[p.cooperator_id];
    if (!c) return p.payment_reference;
    return c.type === 'personne_morale' ? c.legal_name : `${c.first_name || ''} ${c.last_name || ''}`.trim();
  };

  return (
    <div data-testid="cmc-attestations-page">
      <CmcPageHeader
        title="Attestations CMC"
        subtitle="Documents officiels avec QR de vérification publique"
        actions={
          <div className="flex items-center gap-2">
            <Button
              onClick={doBatchAttestPaidPayments}
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              disabled={batchBusy || payments.length === 0}
              data-testid="att-batch-attest-paid"
            >
              {batchBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <ShieldCheck size={14} className="mr-2" />}
              Attester tous ({payments.length})
            </Button>
            <Button onClick={() => { setForm(emptyForm); setDialogOpen(true); }} className="bg-white text-[#D5007D] hover:bg-white/90 font-bold" data-testid="att-create-btn">
              <Plus size={16} className="mr-2" /> Nouvelle attestation
            </Button>
          </div>
        }
      />

      {selectedIds.size > 0 && (
        <Card className="border-[#2D5A4C] bg-[#F0F8E4] mb-4" data-testid="batch-toolbar">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <p className="text-sm font-bold text-[#2D5A4C]">{selectedIds.size} sélectionnée(s)</p>
            <div className="flex-1"></div>
            <Button
              size="sm"
              onClick={doBatchIssue}
              disabled={batchBusy || selectedByStatus('draft').length === 0}
              className="bg-[#2D5A4C] hover:bg-[#1E4136]"
              data-testid="batch-issue-btn"
            >
              {batchBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <ShieldCheck size={12} className="mr-1" />}
              Émettre ({selectedByStatus('draft').length})
            </Button>
            <Button
              size="sm"
              onClick={() => { setBatchRevokeReason(''); setBatchRevokeOpen(true); }}
              disabled={batchBusy || selectedByStatus('issued').length === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="batch-revoke-btn"
            >
              <Prohibit size={12} className="mr-1" />
              Révoquer ({selectedByStatus('issued').length})
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="batch-clear-btn">Effacer</Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
          ) : items.length === 0 ? (
            <CmcEmpty icon={ShieldCheck} message="Aucune attestation" hint="Créez une attestation à partir d'un paiement marqué payé" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F5] text-[11px] uppercase tracking-wide text-[#5E6D66]">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-medium w-8">
                      <Checkbox
                        checked={items.length > 0 && items.every((a) => selectedIds.has(a.id))}
                        onCheckedChange={(v) => {
                          if (v) setSelectedIds(new Set(items.map((a) => a.id)));
                          else clearSelection();
                        }}
                        data-testid="batch-select-all"
                      />
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">Numéro</th>
                    <th className="text-left px-4 py-2.5 font-medium">Bénéficiaire</th>
                    <th className="text-left px-4 py-2.5 font-medium">Titre</th>
                    <th className="text-left px-4 py-2.5 font-medium">Statut</th>
                    <th className="text-left px-4 py-2.5 font-medium">Émise</th>
                    <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0EFEB]">
                  {items.map((a) => (
                    <tr key={a.id} className={`hover:bg-[#FAFAF8] ${selectedIds.has(a.id) ? 'bg-[#F0F8E4]' : ''}`} data-testid={`att-row-${a.id}`}>
                      <td className="px-3 py-3">
                        <Checkbox
                          checked={selectedIds.has(a.id)}
                          onCheckedChange={() => toggleSelect(a.id)}
                          data-testid={`att-select-${a.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{a.attestation_number}</td>
                      <td className="px-4 py-3 text-[#1A2421]">{coopNameByPaymentId(a.payment_id)}</td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{a.title || '-'}</td>
                      <td className="px-4 py-3">
                        <CmcStatusBadge status={a.status} label={ATTESTATION_STATUSES.find((s) => s.value === a.status)?.label} />
                      </td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{a.issued_at ? fmtDate(a.issued_at) : '-'}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {a.status === 'draft' && (
                          <Button size="sm" className="bg-[#2D5A4C] hover:bg-[#1E4136] mr-1" onClick={() => onIssue(a)} data-testid={`att-issue-${a.id}`}>
                            <ShieldCheck size={14} className="mr-1" /> Émettre
                          </Button>
                        )}
                        {a.status === 'issued' && (
                          <>
                            <a href={getAttestationHtmlUrl(a.id)} target="_blank" rel="noopener noreferrer" data-testid={`att-html-${a.id}`}>
                              <Button size="sm" variant="ghost" title="Voir le HTML"><Eye size={14} /></Button>
                            </a>
                            <a href={getAttestationPdfUrl(a.id)} target="_blank" rel="noopener noreferrer" data-testid={`att-pdf-${a.id}`}>
                              <Button size="sm" variant="ghost" title="PDF"><Download size={14} /></Button>
                            </a>
                            <Button size="sm" variant="ghost" onClick={() => setQrDialog(a)} title="QR code" data-testid={`att-qr-${a.id}`}>
                              <QrCode size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setRevokeReason(''); setRevokeDialog(a); }} title="Révoquer" data-testid={`att-revoke-${a.id}`}>
                              <Prohibit size={14} />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="att-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle attestation</DialogTitle>
            <DialogDescription>Émise à partir d'un paiement marqué « payé ».</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Paiement payé *</Label>
              <Select value={form.payment_id} onValueChange={(v) => setForm({ ...form, payment_id: v })}>
                <SelectTrigger data-testid="att-form-payment"><SelectValue placeholder="Choisir un paiement payé" /></SelectTrigger>
                <SelectContent>
                  {payments.length === 0 && <SelectItem value="none" disabled>Aucun paiement payé disponible</SelectItem>}
                  {payments.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.payment_reference} — {coopNameByPaymentId(p.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Titre du document</Label>
              <Input data-testid="att-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Visibilité du montant (vérification publique)</Label>
              <Select value={form.public_amount_visibility} onValueChange={(v) => setForm({ ...form, public_amount_visibility: v })}>
                <SelectTrigger data-testid="att-form-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="masked">Masqué (recommandé)</SelectItem>
                  <SelectItem value="range">Plage de montant</SelectItem>
                  <SelectItem value="visible">Visible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="att-cancel-btn">Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="att-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer (brouillon)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrDialog} onOpenChange={(o) => !o && setQrDialog(null)}>
        <DialogContent className="max-w-md" data-testid="att-qr-dialog">
          <DialogHeader>
            <DialogTitle>QR de vérification</DialogTitle>
            <DialogDescription>{qrDialog?.attestation_number}</DialogDescription>
          </DialogHeader>
          {qrDialog && (
            <div className="flex flex-col items-center gap-3 py-4">
              <img
                src={getAttestationQrUrl(qrDialog.id)}
                alt="QR code"
                className="w-56 h-56 border border-[#E8E6E1] rounded-md p-2"
                data-testid="att-qr-image"
              />
              {qrDialog.verification_url && (
                <a href={qrDialog.verification_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#2D5A4C] underline break-all" data-testid="att-verify-url">
                  {qrDialog.verification_url}
                </a>
              )}
              {qrDialog.document_hash && (
                <div className="text-[10px] font-mono text-[#5E6D66] break-all text-center">
                  SHA-256: {qrDialog.document_hash.slice(0, 32)}...
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!revokeDialog} onOpenChange={(o) => !o && setRevokeDialog(null)}>
        <DialogContent className="max-w-md" data-testid="att-revoke-dialog">
          <DialogHeader>
            <DialogTitle>Révoquer l'attestation</DialogTitle>
            <DialogDescription>Cette action est tracée dans le journal d'audit.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motif *</Label>
            <Input data-testid="att-revoke-reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeDialog(null)} data-testid="att-revoke-cancel">Annuler</Button>
            <Button onClick={onRevoke} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="att-revoke-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Révoquer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={batchRevokeOpen} onOpenChange={setBatchRevokeOpen}>
        <DialogContent className="max-w-md" data-testid="batch-revoke-dialog">
          <DialogHeader>
            <DialogTitle>Révoquer {selectedByStatus('issued').length} attestation(s)</DialogTitle>
            <DialogDescription>Cette action est tracée dans le journal d'audit pour chaque attestation.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Motif commun *</Label>
            <Input
              data-testid="batch-revoke-reason"
              value={batchRevokeReason}
              onChange={(e) => setBatchRevokeReason(e.target.value)}
              placeholder="Ex : Erreur de rédaction — remplacement en cours"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchRevokeOpen(false)}>Annuler</Button>
            <Button
              onClick={doBatchRevoke}
              disabled={batchBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="batch-revoke-confirm"
            >
              {batchBusy ? <Loader2 size={14} className="animate-spin" /> : 'Révoquer tout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
